import {
  KIND, HeraldRelay, queryMany, parseUri, parseChannel, parseManifest, verifySenderChain,
  buildSubscribe, buildUnsubscribe, buildAbuseReport, buildDeliveryReceipt, openDirected,
  readBroadcast, requiredScopeFor, verifyDomainBinding, auditCap, formatCap, isExpired,
  getPublicKey, normalizePubkey, npubEncode, checkEvent, tagValue, now, parseRegistryEntry,
  checkAttachment, hashBlob, decryptBlob, httpBaseFromRelayUrl,
} from '@herald/core';

/**
 * Subscriber-side SDK.
 *
 * The design rule here: the client trusts exactly one thing, the root key that
 * the user scanned. Everything else -- the relay, the manifest, the channel
 * definition, the sending key, the domain -- is verified back to that key or
 * shown to the user as unverified. A relay is treated as a hostile cache.
 */
export class HeraldClient {
  /** @param {{secretKey:Uint8Array|string, relays?:string[], wellKnownOrigin?:string}} config */
  constructor(config) {
    this.secretKey = config.secretKey;
    this.pubkey = getPublicKey(config.secretKey);
    this.relays = config.relays ?? [];
    // dev-only: lets the PoC verify domain binding against a local relay
    this.wellKnownOrigin = config.wellKnownOrigin ?? null;
    this._connections = new Map();
  }

  get npub() { return npubEncode(this.pubkey); }

  #relay(url, authenticated = false) {
    const key = `${url}|${authenticated}`;
    if (!this._connections.has(key)) {
      this._connections.set(key, new HeraldRelay(url, authenticated ? { authKey: this.secretKey } : {}));
    }
    return this._connections.get(key);
  }

  /**
   * Resolve everything needed to make an informed consent decision.
   *
   * This is the screen that replaces "click here to subscribe" -- it is the
   * only place a user can be socially engineered, so every claim it returns is
   * labelled verified or unverified.
   */
  async inspect(uriOrTarget) {
    const target = typeof uriOrTarget === 'string' ? parseUri(uriOrTarget) : uriOrTarget;
    const relays = target.relays?.length ? target.relays : this.relays;
    if (!relays.length) throw new Error('no relay to query: the invite carried none and none are configured');

    const events = await queryMany(relays, [
      { kinds: [KIND.SENDER_PROFILE, KIND.MANIFEST, KIND.CHANNEL], authors: [target.root], limit: 200 },
      // Any operator's admission decision about this sender. Which operators
      // are worth trusting is the caller's judgement, not this SDK's.
      { kinds: [KIND.REGISTRY_ENTRY], '#d': [target.root], limit: 8 },
    ]);

    const warnings = [];
    const valid = events.filter((e) => {
      if (checkEvent(e).ok) return true;
      warnings.push('a relay served an event with an invalid signature; it was discarded');
      return false;
    });

    // Both must be signed by the key the user actually scanned. The channel
    // check below was already doing this; the manifest was not, which let a
    // relay supply its own delegation set -- ranking its relays first and
    // presenting them to the user as the root-signed ones.
    const profileEvent = newest(valid.filter(
      (e) => e.kind === KIND.SENDER_PROFILE && e.pubkey === target.root,
    ));
    const manifestEvent = newest(valid.filter(
      (e) => e.kind === KIND.MANIFEST && e.pubkey === target.root,
    ));
    if (valid.some((e) => e.kind === KIND.MANIFEST && e.pubkey !== target.root)) {
      warnings.push('a relay served a sender manifest signed by some other key; it was discarded');
    }
    const profile = profileEvent ? safeJson(profileEvent.content) : null;
    const manifest = manifestEvent ? parseManifest(manifestEvent) : null;

    const channels = [];
    for (const event of valid.filter((e) => e.kind === KIND.CHANNEL)) {
      // Channel definitions must come from the root key: a delegated marketing
      // key must not be able to redefine the terms users consent to.
      if (event.pubkey !== target.root) {
        warnings.push(`channel definition signed by a non-root key was ignored`);
        continue;
      }
      const channel = parseChannel(event);
      channels.push({ ...channel, capHuman: formatCap(channel.cap), event });
    }

    // Bidirectional domain binding. A domain claimed only in the manifest
    // proves nothing, so an unconfirmed claim is reported as unverified.
    const claimedDomain = manifest?.domain ?? target.domain ?? profile?.domain ?? null;
    let domainVerified = false;
    let domainError = null;
    if (claimedDomain) {
      const res = await verifyDomainBinding({
        domain: claimedDomain, rootPubkey: target.root, origin: this.wellKnownOrigin,
      });
      domainVerified = res.ok;
      domainError = res.ok ? null : res.error;
    } else {
      domainError = 'this sender does not claim a domain, so its real-world identity cannot be checked';
    }

    if (!manifestEvent) warnings.push('no sender manifest published: only the root key itself can be trusted to sign');
    if (!channels.length) warnings.push('this sender publishes no channel definitions');
    if (target.channel && !channels.some((c) => c.slug === target.channel)) {
      warnings.push(`the invite points at channel "${target.channel}", which this sender does not publish`);
    }

    /**
     * Relay preference: the manifest is signed by the root key, a QR code is
     * not. A crafted invite could otherwise pin a subscriber to a relay the
     * attacker controls -- which cannot forge messages, but can withhold them
     * and serve a stale channel definition to hide that the terms changed.
     * Signed relays therefore come first; the invite's hints are kept as a
     * fallback, since a brand-new sender may have no manifest yet.
     */
    /**
     * A registry admission, if this relay's operator publishes one.
     *
     * Independent of domain binding and does not replace it: binding answers
     * "is this really that company", an admission answers "did the operator I
     * trust vet them". A client that has no operator it trusts should ignore
     * this entirely, which is why it is reported rather than acted upon.
     */
    const admissions = valid
      .filter((e) => e.kind === KIND.REGISTRY_ENTRY && tagValue(e, 'd') === target.root)
      .sort((a, b) => b.created_at - a.created_at);
    const admission = admissions.length
      ? { ...parseRegistryEntry(admissions[0]), event: admissions[0] }
      : null;
    if (admission?.status === 'suspended') {
      warnings.push(`the relay operator has suspended this sender${admission.note ? `: ${admission.note}` : ''}`);
    }

    const signedRelays = (manifest?.relays ?? []).filter((r) => /^wss?:\/\//i.test(r));
    const effectiveRelays = [...new Set([...signedRelays, ...relays])];
    if (signedRelays.length && relays.some((r) => !signedRelays.includes(r))) {
      warnings.push('the invite pointed at a relay the sender has not signed for; the signed relays are preferred');
    }

    return {
      root: target.root,
      npub: npubEncode(target.root),
      relays: effectiveRelays,
      inviteRelays: relays,
      signedRelays,
      profile,
      manifest,
      manifestEvent,
      channels,
      selected: target.channel ? channels.find((c) => c.slug === target.channel) ?? null : null,
      requestedChannel: target.channel,
      /**
       * True when the invite named a channel this sender does not publish.
       * HP-03 requires the consent screen to say so: silently offering a
       * different channel would have the user sign terms they never chose.
       */
      requestedChannelMissing: !!(target.channel && !channels.some((c) => c.slug === target.channel)),
      token: target.token ?? null,
      domain: claimedDomain,
      domainVerified,
      domainError,
      admission,
      warnings,
    };
  }

  /**
   * Sign and publish a subscription. Returns the signed consent plus the
   * sender's receipt if it arrives promptly.
   */
  async subscribe({ channel, relays = this.relays, ref = null, waitForReceiptMs = 3000 }) {
    if (!channel) throw new Error('subscribe needs a parsed channel definition');
    if (channel.privacy === 'identified' && !ref && channel.type !== 'newsletter') {
      // Not an error: transactional channels usually need a sender-side
      // reference, but the protocol does not require one.
    }
    const event = buildSubscribe({ channel, relay: relays[0], ref }, this.secretKey);

    const results = [];
    for (const url of relays) {
      const conn = this.#relay(url, true);
      try {
        await conn.authenticate(this.secretKey);
        results.push({ relay: url, ...(await conn.publish(event)) });
      } catch (err) {
        results.push({ relay: url, ok: false, message: err.message });
      }
    }
    const accepted = results.some((r) => r.ok);

    let receipt = null;
    if (accepted && waitForReceiptMs > 0) {
      receipt = await this.#awaitReceipt(event, relays[0], waitForReceiptMs);
    }
    return { subscribe: event, results, accepted, receipt };
  }

  async #awaitReceipt(subscribeEvent, url, timeoutMs) {
    const conn = this.#relay(url, true);
    return new Promise((resolve) => {
      let done = false;
      const finish = (value) => { if (!done) { done = true; stop?.(); resolve(value); } };
      const timer = setTimeout(() => finish(null), timeoutMs);
      let stop;
      conn.subscribe(
        { kinds: [KIND.CONSENT_RECEIPT], '#p': [this.pubkey], '#e': [subscribeEvent.id] },
        (event) => { clearTimeout(timer); finish(event); },
      ).then((fn) => { stop = fn; if (done) fn(); }).catch(() => finish(null));
    });
  }

  /**
   * Fetch the sender's countersignature for an existing subscription.
   *
   * Receipts are issued asynchronously, so a client that only looked once at
   * subscribe time would permanently believe it holds no proof of consent.
   */
  async fetchReceipt({ subscribeId, relays = this.relays }) {
    for (const url of relays) {
      const conn = this.#relay(url, true);
      try {
        await conn.authenticate(this.secretKey);
        const events = await conn.query({
          kinds: [KIND.CONSENT_RECEIPT], '#p': [this.pubkey], '#e': [subscribeId], limit: 1,
        });
        if (events.length) return events[0];
      } catch { /* try the next relay */ }
    }
    return null;
  }

  async unsubscribe({ sender, slug, subscribeId, reason = '', relays = this.relays }) {
    const event = buildUnsubscribe({ sender, slug, subscribeId, reason }, this.secretKey);
    const results = [];
    for (const url of relays) {
      const conn = this.#relay(url, true);
      try {
        await conn.authenticate(this.secretKey);
        results.push({ relay: url, ...(await conn.publish(event)) });
      } catch (err) {
        results.push({ relay: url, ok: false, message: err.message });
      }
    }
    return { unsubscribe: event, results, accepted: results.some((r) => r.ok) };
  }

  /**
   * Verify a message against the root key the user actually scanned.
   * @returns {{ok:true, via:string}|{ok:false, error:string}}
   */
  verifyMessage(event, { root, manifestEvent }) {
    const structural = checkEvent(event);
    if (!structural.ok) return { ok: false, error: structural.error };
    return verifySenderChain(event, root, manifestEvent, requiredScopeFor(event));
  }

  /**
   * Fetch and verify a channel's messages.
   *
   * Broadcasts are pulled anonymously: no AUTH, no identity revealed, so the
   * sender cannot learn who read the newsletter. Directed messages require
   * AUTH because they are addressed to us by name.
   */
  async fetchMessages({ root, manifestEvent, channel, relays = this.relays, since = 0, includeDirected = true }) {
    const rootHex = normalizePubkey(root);
    const senderKeys = [rootHex, ...(manifestEvent ? parseManifest(manifestEvent).keys.map((k) => k.pubkey) : [])];

    const broadcasts = await queryMany(relays, [{
      kinds: [KIND.BROADCAST], authors: senderKeys, '#c': [channel], since, limit: 200,
    }]);

    let directed = [];
    if (includeDirected) {
      for (const url of relays) {
        const conn = this.#relay(url, true);
        try {
          await conn.authenticate(this.secretKey);
          directed.push(...await conn.query({
            kinds: [KIND.DIRECTED], '#p': [this.pubkey], '#c': [channel], since, limit: 200,
          }));
        } catch { /* relay may not hold our directed messages */ }
      }
    }

    const messages = [];
    for (const event of [...broadcasts, ...directed]) {
      // The channel is the unit of consent, so an event must actually carry the
      // channel we asked for. A relay could otherwise move a broadcast from a
      // channel the user never subscribed to into a subscribed inbox, where it
      // would render as verified and be counted in that channel's cap audit.
      if (tagValue(event, 'c') !== channel) continue;

      const verdict = this.verifyMessage(event, { root: rootHex, manifestEvent });
      const base = {
        id: event.id, kind: event.kind, createdAt: event.created_at, event,
        verified: verdict.ok, via: verdict.ok ? verdict.via : null,
        error: verdict.ok ? null : verdict.error,
        expired: isExpired(event),
        direct: event.kind === KIND.DIRECTED,
      };
      if (!verdict.ok) { messages.push({ ...base, subject: '(unverified)', body: '' }); continue; }
      try {
        const content = event.kind === KIND.DIRECTED
          ? openDirected(event, this.secretKey)
          : readBroadcast(event);
        messages.push({ ...base, ...content });
      } catch (err) {
        messages.push({ ...base, subject: '(could not open)', body: '', error: err.message, verified: false });
      }
    }

    const deduped = [...new Map(messages.map((m) => [m.id, m])).values()]
      .sort((a, b) => b.createdAt - a.createdAt);
    return deduped;
  }

  /**
   * Fetch an attachment and verify it against the signed descriptor.
   *
   * The hash lives inside the signed event (or the signed ciphertext), so this
   * check is what makes a relay unable to substitute content -- there is no
   * second signature to verify, and none is needed.
   *
   * @returns {Promise<{bytes:Uint8Array, filename:string, mime:string}>}
   */
  async downloadAttachment(attachment, { relay = this.relays[0] } = {}) {
    const problem = checkAttachment(attachment);
    if (problem) throw new Error(`refusing to fetch: ${problem}`);

    const res = await fetch(`${httpBaseFromRelayUrl(relay)}/blob/${attachment.hash}`);
    if (!res.ok) throw new Error(`attachment not available on ${relay} (HTTP ${res.status})`);
    const stored = new Uint8Array(await res.arrayBuffer());

    if (hashBlob(stored) !== attachment.hash) {
      throw new Error('attachment failed its integrity check: the relay served different bytes than the sender signed');
    }
    const bytes = attachment.key ? decryptBlob(stored, attachment.key) : stored;
    return { bytes, filename: attachment.filename, mime: attachment.mime };
  }

  /** Independent cap audit: did this sender keep the promise it published? */
  auditChannel(messages, cap) {
    const verified = messages.filter((m) => m.verified);
    const audit = auditCap(verified.map((m) => m.event), cap);
    return {
      ...audit,
      declared: formatCap(cap),
      breach: !audit.ok
        ? `sender published ${audit.worst.count} messages in a window where it promised at most ${audit.cap.count}`
        : null,
    };
  }

  /** Live subscription to a channel. Broadcasts only, so it stays anonymous. */
  async watch({ root, manifestEvent, channel, relays = this.relays, onMessage }) {
    const rootHex = normalizePubkey(root);
    const senderKeys = [rootHex, ...(manifestEvent ? parseManifest(manifestEvent).keys.map((k) => k.pubkey) : [])];
    const stops = [];
    for (const url of relays) {
      const conn = this.#relay(url, false);
      stops.push(await conn.subscribe(
        { kinds: [KIND.BROADCAST], authors: senderKeys, '#c': [channel], since: now() },
        (event) => {
          const verdict = this.verifyMessage(event, { root: rootHex, manifestEvent });
          onMessage({
            ...(verdict.ok ? readBroadcast(event) : { subject: '(unverified)', body: '' }),
            id: event.id, createdAt: event.created_at, verified: verdict.ok,
            error: verdict.ok ? null : verdict.error, event,
          });
        },
      ));
    }
    return () => stops.forEach((stop) => stop());
  }

  async acknowledge({ messageId, sender, status = 'read', relays = this.relays }) {
    const event = buildDeliveryReceipt({ messageId, sender, status }, this.secretKey);
    for (const url of relays) {
      const conn = this.#relay(url, true);
      try { await conn.authenticate(this.secretKey); await conn.publish(event); } catch { /* best effort */ }
    }
    return event;
  }

  async report({ sender, slug, reason, offendingEvent, receipt, relays = this.relays }) {
    const event = buildAbuseReport({ sender, slug, reason, offendingEvent, receipt }, this.secretKey);
    const results = [];
    for (const url of relays) {
      try { results.push({ relay: url, ...(await this.#relay(url, false).publish(event)) }); }
      catch (err) { results.push({ relay: url, ok: false, message: err.message }); }
    }
    return { report: event, results };
  }

  close() {
    for (const conn of this._connections.values()) conn.close();
    this._connections.clear();
  }
}

function newest(events) {
  return events.sort((a, b) => b.created_at - a.created_at)[0] ?? null;
}

function safeJson(text) {
  try { return JSON.parse(text); } catch { return null; }
}
